<template>
  <section>
    <div class="row">
      <z-tabs v-model="currentTab" :tabs="tabs" @change="showTab" />
    </div>
    <div class="row p-3" v-if="currentTab === 'Simulator'">
      <div class="col">
        <!-- Heartrate Simulator -->
        <div class="row">
          <div class="col">
            <p>
              This will simulate the capture of heartrate data from a heart
              monitor for a given user. It will broadcast this data every second
              to any client "listening" on another browser page/tab when viewing
              the Assessment tab in that User's profile.
            </p>
            <p>
              For a given user, provide a valid Session Id below. In a separate
              browser page/tab, navigate to that same user's profile and view
              the Assessment tab.
            </p>
            <p>
              Click the "Pair" button to simulate starting the Assessment App.
              Then, click the "Start Sim" button (below) to start sending HR
              data. Next, click the "Capture" button in the Assessment tab (in
              the other browser window). This will prompt the Assessment tab to
              display the HR data.
            </p>
            <p>
              When finished, click the "Finish" button in the Assessment tab to
              stop the simulation and perform any calculations. The "Cancel"
              button will stop the simulation.
            </p>
            <p>
              In the future, the "Pair" button will launch the Android
              Assessment application. Once the device is paired, it'll start
              sending HR data. Use the "Start" button to collect the data. Then,
              click the "Finish" button to stop the data collection and perform
              any calculations.
            </p>
            <p>
              IMPORTANT! Leave this page open for the simulator to send data.
            </p>
          </div>
        </div>
        <!-- Sample Data -->
        <div class="row">
          <div class="col-2">
            <z-input v-model="userId" label="User ID" type="number" />
          </div>
          <div class="col-2">
            <z-input v-model="sessionId" label="Session ID" type="number" />
          </div>
          <div class="col-1">
            <i v-if="isData" class="fas fa-check custom-check"></i>
          </div>
          <div class="col-7 custom-start-sim">
            <button
              class="btn btn-primary custom-start-button"
              @click="startSim"
            >
              Start Sim
            </button>
            <div class="custom-heartrate-wrapper">
              <div v-show="isStarted" class="custom-heartrate">
                <span>{{ heartrate }} bpm</span>
                <i class="fas fa-heartbeat fa-pulse custom-heart"></i>
              </div>
            </div>
            <button
              class="btn btn-primary custom-start-button"
              @click="cancelSim"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="row p-3" v-if="currentTab === 'Monitor'">
      <div class="col">
        <!-- Heartrate Monitor -->
        <div class="row mt-4">
          <div class="col">
            <p>
              You may also use your heartrate monitor device to capture data.
              This will require you to apply your monitor and pair the device by
              clicking the "Pair Device" button below. After pairing, your
              monitor will start collecting data.
            </p>
            <p>
              In a separate browser/tab, navigate to your Profile > Assessment
              screen and click the "Save" button to start capturing HR data.
            </p>
            <p>IMPORTANT! Leave this page open while the monitor is active.</p>
            <p>
              To disconnect the device, either turn off the device or use the
              "Disconnect Device" button below.
            </p>
          </div>
        </div>
        <div class="row justify-content-center">
          <div class="col-12">
            <div class="row">
              <div class="col-2">
                <z-input v-model="userId" label="User ID" type="number" />
              </div>
              <div class="col pt-4">
                <button class="btn btn-primary" @click="pairMonitor">
                  Pair Device
                </button>
                <button class="btn btn-primary ms-2" @click="disconnectMonitor">
                  Disconnect Device
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import ZTabs from '@/Components/ZElements/Tabs'
import ZInput from '@/Components/ZElements/Input'
import ZSelect from '@/Components/ZElements/Select'

const HR_BATTERY_SERVICE = '0000180F-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_SYSTEM = '00002a23-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_MODEL_NUM = '00002a24-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_FIRMWARE_REV = '00002a26-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_HARDWARE_REV = '00002a27-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_SOFTWARE_REV = '00002a28-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_MANUFACTURER = '00002a29-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_MEASUREMENT = '00002a37-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_SENSOR_LOCATION = '00002a38-0000-1000-8000-00805f9b34fb'
const HR_CHARACTERISTIC_BATTERY_LEVEL = '00002a19-0000-1000-8000-00805f9b34fb'
const HR_MONITOR_CONFIG = '00002902-0000-1000-8000-00805f9b34fb'
const HR_MEMORY_RESPONSE = 'f0ba5302-6cac-4c99-9089-4b0a1df45002'
const HR_MEMORY_WRITE = 'f0ba5300-6cac-4c99-9089-4b0a1df45002'

export default {
  name: 'heartrate-sim',
  components: {
    ZTabs,
    ZInput,
    ZSelect,
  },
  data() {
    return {
      tabs: ['Simulator', 'Monitor'],
      currentTab: 'Simulator',
      isContinue: false,
      simTimer: null,
      userId: 218,
      sessionId: 11127449,
      isData: false,
      isGetData: false,
      protocol: 1,
      monitorDevice: null,
      isStarted: false,
      heartrate: 0,
    }
  },
  mounted() {
    this.init()
  },
  computed: {
    ...mapGetters('users', ['profile']),
    ...mapGetters('usercardio', ['memberSessionHeartRates']),
  },
  methods: {
    ...mapActions('usercardio', ['getMemberSessionHeartRates']),
    async init() {
      this.currentTab = 'Simulator'
      this.isStarted = false
      this.heartrate = 0
      this.channel = Echo.join('direct').listenForWhisper(
        'heartrateaction',
        (e) => {
          if (parseInt(e.userId) === parseInt(this.userId)) {
            if (e.action === 'start') {
              this.protocol = e.protocol
              if (this.currentTab === 'Monitor') {
                // this.pairMonitor()
              } else {
                this.startSim()
              }
            } else if (e.action === 'stop') {
              if (this.currentTab === 'Monitor') {
                this.stopMonitor()
              } else {
                this.stopSim()
              }
            }
          }
        }
      )
    },
    showTab(tab) {
      this.currentTab = tab
      console.log('Showing Tab', this.currentTab)
      if (this.currentTab === 'Monitor') {
        this.stopSim()
      }
    },
    async getSampleData() {
      this.isData = false
      this.isGetData = true
      let data = {
        id: this.userId,
        session: this.sessionId,
      }
      await this.getMemberSessionHeartRates(data)
      this.isGetData = false
      if (this.memberSessionHeartRates.length > 0) {
        this.isData = true
      }
    },
    async startSim() {
      let $this = this
      let cnt = 0
      this.isContinue = true
      this.heartrate = 0
      await this.getSampleData()
      if (this.isData) {
        this.init()
        this.isStarted = true
        this.simTimer = setInterval(() => {
          if (cnt >= $this.memberSessionHeartRates.length) {
            cnt = 0
          } else {
            cnt++
          }
          $this.heartrate = $this.memberSessionHeartRates[cnt].HeartRate
          $this.channel.whisper('heartrate', {
            userId: $this.userId,
            data: $this.heartrate,
            action: 'start',
            protocol: this.protocol,
          })
        }, 1000)
      }
    },
    cancelSim() {
      this.isContinue = false
      this.isStarted = false
      clearInterval(this.simTimer)
      this.simTimer = null
      this.channel.whisper('heartrate', {
        userId: this.userId,
        data: 0,
        action: 'stop2',
        protocol: this.protocol,
      })
    },
    stopSim() {
      // this.currentTab = 'Simulator'
      this.isContinue = false
      this.isStarted = false
      clearInterval(this.simTimer)
      this.simTimer = null
      this.channel.whisper('heartrate', {
        userId: this.userId,
        data: 0,
        action: 'stop',
        protocol: this.protocol,
      })
    },
    stopMonitor() {
      // this.currentTab = 'Monitor'
      this.isContinue = false
      this.simTimer = null
      this.channel.whisper('heartrate', {
        userId: this.userId,
        data: 0,
        action: 'stop',
        protocol: this.protocol,
      })
    },
    disconnectMonitor() {
      navigator.bluetooth
        .requestDevice({
          acceptAllDevices: true,
          optionalServices: [
            'device_information',
            'heart_rate',
            'battery_service',
          ],
        })
        .then((device) => device.gatt.disconnect())
    },
    pairMonitor() {
      navigator.bluetooth
        .requestDevice({
          acceptAllDevices: true,
          optionalServices: [
            'device_information',
            'heart_rate',
            'battery_service',
          ],
        })
        .then((device) => device.gatt.connect())
        .then((server) => {
          // Getting Service…
          return server.getPrimaryService('heart_rate')
        })
        // --------------------------------------------
        // TO OBTAIN DEVICE CHARACTERISTICS
        .then((service) => {
          console.log('Getting Device Information Characteristics...')
          return service.getCharacteristics()
        })
        .then((characteristics) => {
          if (characteristics) {
            characteristics.forEach((characteristic) => {
              console.log(`Examining Characteristics: ${characteristic.uuid}`)
              switch (characteristic.uuid) {
                case HR_CHARACTERISTIC_MEASUREMENT:
                  console.log(
                    `HR Measurement Characteristic: ${characteristic.uuid}`
                  )
                  characteristic.startNotifications().then((notifier) => {
                    notifier.addEventListener(
                      'characteristicvaluechanged',
                      (event) => {
                        let dv = event.target.value
                        console.log('HR Measurement Notification', dv)
                        const heartrate = dv.getInt8(1)
                        this.channel.whisper('heartrate', {
                          userId: this.userId,
                          data: heartrate,
                          action: 'start',
                          protocol: this.protocol,
                        })
                      }
                    )
                  })
                  break
                case HR_CHARACTERISTIC_SENSOR_LOCATION:
                  console.log(
                    `Body Sensor Location Characteristic: ${characteristic.uuid}`
                  )
                  characteristic.startNotifications().then((notifier) => {
                    notifier.addEventListener(
                      'characteristicvaluechanged',
                      (event) => {
                        let dv = event.target.value
                        console.log('Body Sensor Location Notification', dv)
                        // TODO: parse the results...
                      }
                    )
                  })
                  break
              }
            })
          }
        })
    },
  },
  // beforeUnmount() {
  //   Echo.leave('direct')
  // },
}
</script>
<style lang="scss" scoped>
.custom-title {
  text-align: center;
  font-size: 1.2em;
  font-weight: 600;
  padding: 10px;
}
.custom-check {
  color: green;
  font-size: 1.2em;
  top: 20px;
  position: relative;
}
.custom-chart {
  text-align: center;
}
.custom-start-sim {
  display: inline-flex;
}
.custom-start-button {
  height: 40px !important;
  margin-top: 20px !important;
}
.custom-heartrate-wrapper {
  position: relative;
  width: 180px;
}
.custom-heartrate {
  position: relative;
  top: 20px;
  left: 20px;
  width: 180px;
  span {
    font-size: 1.1em;
    font-weight: 500;
  }
}
.custom-heart {
  font-size: 1.2em;
  color: rgb(201, 0, 0);
  position: absolute;
  top: 0;
  margin-left: 10px;
}
/* Icon pulse */
.fa-pulse {
  display: inline-block;
  -moz-animation: pulse 3s infinite linear;
  -o-animation: pulse 3s infinite linear;
  -webkit-animation: pulse 3s infinite linear;
  animation: pulse 3s infinite linear;
}

@-webkit-keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@-moz-keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@-o-keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@-ms-keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
